/** For a key `K`, yields `K` if the value `V` may not be nullish, else yields never */
type PickNonNullishKey<K, V> = null extends V ? never : undefined extends V ? never : K;

/** For a key `K`, yeilds `K` if the value `V` may *only* be nullish,
 * and not a union of nullish and non-nullish type, else yeilds `never` */
type PickExclusivelyNullishKey<K, V> = Exclude<V, null | undefined> extends never ? K : never;

/** For a key `K`, yields `K` if the value `V` is a union of a nullish and non-nullish type
 * (and thus is not exclusively nullish), else yields `never` */
type PickPossiblyNullishKey<K, V> = PickExclusivelyNullishKey<K, V> extends never
  ? null extends V
    ? K
    : undefined extends V
    ? K
    : never
  : never;

/**
 * An object type where for any property in `T` that may be nullish,
 * that property becomes optional (and cannot be null).
 * If a property can *only* be nullish, that property is excluded entirely.
 */
type OptionalIfNullish<T extends object> = {
  [K in keyof T as PickNonNullishKey<K, T[K]>]: T[K];
} & {
  [K in keyof T as PickPossiblyNullishKey<K, T[K]>]?: Exclude<T[K], null> | undefined;
};

/**
 * Flattens an intersected object type, and also expands it, such that the resulting type represents the actual type
 * and not just the input type wrapped by this type.
 *
 * For example, given some input type `{a: number | null} & {b: string}`, instead of the type appearing in tooltips as:
 *
 * `
 * FlattenAndExpand<{a: number} & {b: string}>
 * `
 *
 * It will instead appear as the actual final output type:
 *
 * `
 * {a: number, b: string}
 * `
 */
type FlattenAndExpand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;

/** Filters out properties with values of `undefined` or `null` from an object */
export function filterNullishProperties<Obj extends object>(
  obj: Obj
): FlattenAndExpand<OptionalIfNullish<Obj>> {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}
